import { useState, useRef, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SessionProvider } from "../lib/session";
import { queryKeys } from "../../../shared/api/queryKeys";
import { listCoachingSessions } from "../../../shared/api/endpoints/coaching";
import { getCurrentUser, logout } from "../../../shared/api/endpoints/auth";

// Primary nav: Dashboard · Pipeline · Network · Profile · Resume Coaching (+ Account menu).
const NETWORK_LINKS = [
  { to: "/network/people", label: "People" },
  { to: "/network/companies", label: "Companies" },
  { to: "/network/groups", label: "Groups" },
];

const PIPELINE_LINKS = [
  { to: "/pipeline/tasks", label: "Tasks" },
  { to: "/pipeline/review", label: "Weekly Review" },
];

const PROFILE_LINKS = [
  { to: "/profile", label: "Overview" },
  { to: "/profile-optimizer", label: "LinkedIn Optimizer" },
];

const ACCOUNT_LINKS = [
  { to: "/account/subscription", label: "Subscription" },
  { to: "/account/security", label: "Security" },
  { to: "/account/email-preferences", label: "Email Preferences" },
  { to: "/account/api-keys", label: "API Access" },
];

function isActivePath(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(to + "/");
}

// Resume Coaching destination: the in-progress session if any, else Profile (where
// coaching is started). Sessions are premium-only, so we only query for premium users.
function useCoachingTarget(tier: string): string {
  const { data: sessions = [] } = useQuery({
    queryKey: queryKeys.coaching.sessions({ scope: "nav" }),
    queryFn: () => listCoachingSessions(),
    enabled: tier === "premium",
    staleTime: 60_000,
  });
  const inProgress = sessions.find((s) => s.status === "active" || s.status === "paused");
  return inProgress ? `/coaching/${inProgress.uuid}` : "/profile";
}

function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  return { open, setOpen, ref };
}

function NavLink({ to, label }: { to: string; label: string }) {
  const { pathname } = useLocation();
  const active = isActivePath(pathname, to);
  return (
    <Link
      to={to}
      className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
        active ? "bg-blue-50 font-medium text-blue-700" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      }`}
    >
      {label}
    </Link>
  );
}

// A primary nav item that navigates on click AND reveals a dropdown on hover.
function NavWithDropdown({ to, label, items }: { to: string; label: string; items: { to: string; label: string }[] }) {
  const { pathname } = useLocation();
  const active = isActivePath(pathname, to) || items.some((i) => isActivePath(pathname, i.to));
  return (
    <div className="group relative">
      <Link
        to={to}
        className={`block rounded-md px-3 py-1.5 text-sm transition-colors ${
          active ? "bg-blue-50 font-medium text-blue-700" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        }`}
      >
        {label}
      </Link>
      <div className="invisible absolute left-0 top-full z-50 w-44 rounded-md border border-gray-200 bg-white py-1 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100">
        {items.map(({ to: t, label: l }) => (
          <Link key={t} to={t} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            {l}
          </Link>
        ))}
      </div>
    </div>
  );
}

function ResumeCoachingNavLink({ tier }: { tier: string }) {
  const target = useCoachingTarget(tier);
  const { pathname } = useLocation();
  const active = pathname.startsWith("/coaching");
  return (
    <Link
      to={target}
      className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
        active ? "bg-blue-50 font-medium text-blue-700" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      }`}
    >
      Resume Coaching
    </Link>
  );
}

function AccountMenu() {
  const { open, setOpen, ref } = useDropdown();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const qc = useQueryClient();

  const logoutMut = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      qc.clear();
      navigate("/login");
    },
  });

  const isActive = pathname.startsWith("/account");

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`text-sm transition-colors ${
          isActive ? "font-medium text-blue-700" : "text-gray-500 hover:text-gray-700"
        }`}
      >
        Account
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-52 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {ACCOUNT_LINKS.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(false)}
            >
              {label}
            </Link>
          ))}
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={() => {
              setOpen(false);
              logoutMut.mutate();
            }}
            disabled={logoutMut.isPending}
            className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            {logoutMut.isPending ? "Signing out..." : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}

function MobileDrawer({ open, onClose, tier }: { open: boolean; onClose: () => void; tier: string }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const coachingTarget = useCoachingTarget(tier);

  const logoutMut = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      qc.clear();
      onClose();
      navigate("/login");
    },
  });

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const primary = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/pipeline", label: "Pipeline" },
  ];

  const linkCls = (to: string) =>
    `flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActivePath(pathname, to)
        ? "bg-blue-50 text-blue-700"
        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
    }`;

  return (
    <div className="fixed inset-0 z-50 sm:hidden">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-white flex flex-col shadow-2xl">
        <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4">
          <Link to="/dashboard" className="text-xl font-bold text-gray-900" onClick={onClose}>
            ResumeAI
          </Link>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close menu"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          <div className="space-y-0.5 px-2">
            {primary.map(({ to, label }) => (
              <Link key={to} to={to} onClick={onClose} className={linkCls(to)}>
                {label}
              </Link>
            ))}
          </div>

          {/* Pipeline group */}
          <div className="mt-3 border-t border-gray-100 pt-3 px-2">
            <p className="px-1 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Pipeline</p>
            {PIPELINE_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} onClick={onClose} className={linkCls(to)}>
                {label}
              </Link>
            ))}
          </div>

          {/* Network group */}
          <div className="mt-3 border-t border-gray-100 pt-3 px-2">
            <p className="px-1 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Network</p>
            {NETWORK_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} onClick={onClose} className={linkCls(to)}>
                {label}
              </Link>
            ))}
          </div>

          <div className="mt-3 border-t border-gray-100 pt-3 px-2 space-y-0.5">
            <Link to="/profile" onClick={onClose} className={linkCls("/profile")}>
              Profile
            </Link>
            <Link
              to="/profile-optimizer"
              onClick={onClose}
              className={linkCls("/profile-optimizer")}
            >
              LinkedIn Optimizer
            </Link>
            <Link
              to={coachingTarget}
              onClick={onClose}
              className={`flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                pathname.startsWith("/coaching")
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              }`}
            >
              Resume Coaching
            </Link>
          </div>
        </nav>

        {/* Account section */}
        <div className="border-t border-gray-200 py-3">
          <p className="px-5 pb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">Account</p>
          <div className="space-y-0.5 px-2">
            {ACCOUNT_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} onClick={onClose} className={linkCls(to)}>
                {label}
              </Link>
            ))}
            <button
              onClick={() => logoutMut.mutate()}
              disabled={logoutMut.isPending}
              className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              {logoutMut.isPending ? "Signing out..." : "Sign out"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { data: user } = useQuery({
    queryKey: queryKeys.auth.user(),
    queryFn: getCurrentUser,
    staleTime: 5 * 60 * 1000,
  });
  const tier = user?.tier ?? "free_trial";

  return (
    <div className="flex min-h-screen flex-col">
      <nav className="sticky top-0 z-40 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative flex h-16 items-center justify-between">

            {/* Left: hamburger (mobile) OR logo+links (desktop) */}
            <div className="flex items-center gap-6">
              <button
                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 sm:hidden"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <Link to="/dashboard" className="hidden text-xl font-bold text-gray-900 sm:block">
                ResumeAI
              </Link>

              {/* Desktop nav: 5 primary items */}
              <div className="hidden items-center gap-1 sm:flex">
                <NavLink to="/dashboard" label="Dashboard" />
                <NavWithDropdown to="/pipeline" label="Pipeline" items={PIPELINE_LINKS} />
                <NavWithDropdown to="/network/people" label="Network" items={NETWORK_LINKS} />
                <NavWithDropdown to="/profile" label="Profile" items={PROFILE_LINKS} />
                <ResumeCoachingNavLink tier={tier} />
              </div>
            </div>

            {/* Center: logo - mobile only */}
            <Link
              to="/dashboard"
              className="absolute left-1/2 -translate-x-1/2 text-xl font-bold text-gray-900 sm:hidden"
            >
              ResumeAI
            </Link>

            {/* Right: account - desktop only */}
            <div className="hidden items-center gap-4 sm:flex">
              <AccountMenu />
            </div>
          </div>
        </div>
      </nav>

      <MobileDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} tier={tier} />

      <SessionProvider>
        <div className="flex-1 bg-gray-50">
          <Outlet />
        </div>
      </SessionProvider>

      <footer className="border-t border-gray-200 bg-white mt-8">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} ResumeAI. All rights reserved.
            </p>
            <nav className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-500">
              <Link to="/account/subscription" className="hover:text-gray-700">Subscription</Link>
              <Link to="/account/security" className="hover:text-gray-700">Security settings</Link>
              <Link to="/account/email-preferences" className="hover:text-gray-700">Email preferences</Link>
              <span className="hidden sm:inline text-gray-300">|</span>
              <Link to="/legal/privacy" className="hover:text-gray-700">Privacy policy</Link>
              <Link to="/legal/terms" className="hover:text-gray-700">Terms of use</Link>
              <Link to="/legal/security" className="hover:text-gray-700">Security</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
